import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, mediaSrcById } from "../api";
import { useApp } from "../context/AppContext";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { IssueCard, TaskCard } from "../components/Artifacts";
import { useArtifactTranslations } from "../hooks/useArtifactTranslations";
import {
  Search as SearchIcon, MessageSquare, Image as ImageIcon, AlertTriangle, ClipboardList, Mic, FileText, X, Briefcase,
} from "lucide-react";

const EMPTY = { messages: [], photos: [], issues: [], tasks: [] };

function Highlight({ text, q }) {
  if (!text) return null;
  if (!q) return <>{text}</>;
  const lower = text.toLowerCase();
  const ql = q.toLowerCase();
  const parts = [];
  let idx = 0;
  let hit = lower.indexOf(ql, idx);
  while (hit !== -1 && parts.length < 20) {
    if (hit > idx) parts.push(<span key={`p${idx}`}>{text.slice(idx, hit)}</span>);
    parts.push(
      <mark key={`m${hit}`} className="bg-accent/40 text-foreground rounded-[2px] px-0.5">
        {text.slice(hit, hit + q.length)}
      </mark>
    );
    idx = hit + q.length;
    hit = lower.indexOf(ql, idx);
  }
  if (idx < text.length) parts.push(<span key={`e${idx}`}>{text.slice(idx)}</span>);
  return <>{parts}</>;
}

function typeIcon(type) {
  if (type === "voice") return Mic;
  if (type === "document") return FileText;
  if (type === "issue") return AlertTriangle;
  return MessageSquare;
}

function fmtDate(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch (e) {
    return "";
  }
}

export default function SearchPage() {
  const { user, t, lang } = useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") || "";
  const initialJob = searchParams.get("job") || "all";
  const [query, setQuery] = useState(initialQ);
  const [results, setResults] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [filter, setFilter] = useState("all");
  const [jobs, setJobs] = useState([]);
  const [jobFilter, setJobFilter] = useState(initialJob); // "all" | job id
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const lastQueryRef = useRef("");
  const jobFilterRef = useRef(initialJob);
  jobFilterRef.current = jobFilter;

  // Jobs the user can filter by
  useEffect(() => {
    api.get(`/jobs?user_id=${user.id}`).then((res) => setJobs(res.data)).catch(() => {});
  }, [user.id]);

  const runSearch = useCallback(async (q) => {
    const trimmed = (q || "").trim();
    lastQueryRef.current = trimmed;
    if (trimmed.length < 2) {
      setResults(EMPTY);
      setSearched(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const jid = jobFilterRef.current;
      const jobParam = jid && jid !== "all" ? `&job_id=${jid}` : "";
      const res = await api.get(`/search?q=${encodeURIComponent(trimmed)}&user_id=${user.id}${jobParam}`);
      // Ignore stale responses (user kept typing)
      if (lastQueryRef.current !== trimmed) return;
      setResults(res.data || EMPTY);
      setSearched(true);
    } catch (e) {
      setResults(EMPTY);
      setSearched(true);
    } finally {
      if (lastQueryRef.current === trimmed) setLoading(false);
    }
  }, [user.id]);

  // Debounced search on typing + keep query/job in the URL (back nav restores it)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(query);
      const trimmed = query.trim();
      const params = {};
      if (trimmed) params.q = trimmed;
      if (jobFilter && jobFilter !== "all") params.job = jobFilter;
      setSearchParams(params, { replace: true });
    }, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, jobFilter]);

  useEffect(() => {
    inputRef.current?.focus();
    if (initialQ.trim().length >= 2) runSearch(initialQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Issue/task results in the viewer's language (lazy, cached)
  const setIssueResults = useCallback((fn) => {
    setResults((prev) => ({ ...prev, issues: typeof fn === "function" ? fn(prev.issues) : fn }));
  }, []);
  const setTaskResults = useCallback((fn) => {
    setResults((prev) => ({ ...prev, tasks: typeof fn === "function" ? fn(prev.tasks) : fn }));
  }, []);
  useArtifactTranslations(results.issues, setIssueResults, "issue", lang);
  useArtifactTranslations(results.tasks, setTaskResults, "task", lang);

  const counts = useMemo(() => ({
    messages: results.messages.length,
    photos: results.photos.length,
    issues: results.issues.length,
    tasks: results.tasks.length,
  }), [results]);
  const total = counts.messages + counts.photos + counts.issues + counts.tasks;

  const q = query.trim();
  const showSection = (key) => filter === "all" || filter === key;
  const openMessage = (item) => navigate(`/chat/${item.conversation_id}?msg=${item.id}`);
  const refresh = () => runSearch(query);

  return (
    <div className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-6" data-testid="search-page">
      <h1 className="font-display font-bold text-2xl tracking-tight mb-4">{t("global_search")}</h1>

      {/* Search input + job filter */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            data-testid="global-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search_ph")}
            className="pl-9 pr-9 h-11"
          />
          {query && (
            <button
              data-testid="global-search-clear"
              onClick={() => { setQuery(""); inputRef.current?.focus(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
              aria-label="Clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select value={jobFilter} onValueChange={setJobFilter}>
          <SelectTrigger data-testid="search-job-filter" className="h-11 w-full sm:w-56">
            <span className="flex items-center gap-2 min-w-0">
              <Briefcase className="h-4 w-4 text-primary shrink-0" />
              <span className="truncate"><SelectValue /></span>
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="search-job-option-all">{t("all_jobs")}</SelectItem>
            {jobs.map((j) => (
              <SelectItem key={j.id} value={j.id} data-testid={`search-job-option-${j.job_number}`}>
                #{j.job_number} — {j.customer_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Category filter */}
      {searched && total > 0 && (
        <Tabs value={filter} onValueChange={setFilter} className="mb-4">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="all" data-testid="search-filter-all">{t("all")} ({total})</TabsTrigger>
            <TabsTrigger value="messages" data-testid="search-filter-messages" disabled={!counts.messages}>
              {t("messages")} ({counts.messages})
            </TabsTrigger>
            <TabsTrigger value="photos" data-testid="search-filter-photos" disabled={!counts.photos}>
              {t("photos")} ({counts.photos})
            </TabsTrigger>
            <TabsTrigger value="issues" data-testid="search-filter-issues" disabled={!counts.issues}>
              {t("issues")} ({counts.issues})
            </TabsTrigger>
            <TabsTrigger value="tasks" data-testid="search-filter-tasks" disabled={!counts.tasks}>
              {t("tasks")} ({counts.tasks})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Loading skeletons */}
      {loading && (
        <div className="space-y-3" data-testid="search-loading">
          {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />)}
        </div>
      )}

      {/* Hint: type at least 2 chars */}
      {!loading && q.length < 2 && (
        <div className="text-center py-16 text-muted-foreground" data-testid="search-hint">
          <SearchIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{t("search_min_hint")}</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && searched && q.length >= 2 && total === 0 && (
        <div className="text-center py-16 text-muted-foreground" data-testid="search-no-results">
          <SearchIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{t("no_results")}</p>
        </div>
      )}

      {/* Results */}
      {!loading && searched && total > 0 && (
        <div className="space-y-6" data-testid="search-results">
          {/* Messages */}
          {showSection("messages") && counts.messages > 0 && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5" /> {t("messages")}
              </h2>
              <div className="space-y-2">
                {results.messages.map((m) => {
                  const Icon = typeIcon(m.type);
                  return (
                    <Card
                      key={m.id}
                      data-testid="search-result-message"
                      onClick={() => openMessage(m)}
                      className="p-3.5 cursor-pointer hover:bg-muted/40 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold truncate">{m.sender_name}</span>
                            <span className="text-[11px] text-muted-foreground shrink-0">{fmtDate(m.created_at, lang)}</span>
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{m.conversation_name}</div>
                          <div className="text-sm mt-1 line-clamp-2">
                            <Highlight text={(m.translations?.[lang]?.content || "").trim() || m.content} q={q} />
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </section>
          )}

          {/* Photos */}
          {showSection("photos") && counts.photos > 0 && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                <ImageIcon className="h-3.5 w-3.5" /> {t("photos")}
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {results.photos.map((p) => (
                  <button
                    key={p.id}
                    data-testid="search-result-photo"
                    onClick={() => openMessage(p)}
                    className="group relative aspect-square rounded-lg overflow-hidden border bg-muted focus-visible:ring-2 focus-visible:ring-primary"
                    title={p.content || ""}
                  >
                    {p.media_id ? (
                      <img
                        src={mediaSrcById(p.media_id)}
                        alt={p.content || "photo"}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                        <ImageIcon className="h-6 w-6" />
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 pt-4 pb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-[10px] text-white line-clamp-1">{p.sender_name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Issues */}
          {showSection("issues") && counts.issues > 0 && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> {t("issues")}
              </h2>
              <div className="space-y-3">
                {results.issues.map((issue) => (
                  <IssueCard key={issue.id} issue={issue} showJob onChanged={refresh} />
                ))}
              </div>
            </section>
          )}

          {/* Tasks */}
          {showSection("tasks") && counts.tasks > 0 && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                <ClipboardList className="h-3.5 w-3.5" /> {t("tasks")}
              </h2>
              <div className="space-y-3">
                {results.tasks.map((task) => (
                  <TaskCard key={task.id} task={task} showJob onChanged={refresh} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
